import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { useLocation } from '@reach/router';
import styled from '@emotion/styled';
import qs from 'qs';

import useHubspot from '../hooks/use-hubspot';
import { createOptionMap, replaceWithComponent } from '../utilities/misc';
import { colors } from '../theme/colors';
import useUserContext from '../hooks/use-user-context';
import { HUBSPOT_PORTAL_ID } from '../constants';
import { convertPathForTracking } from '../utilities/url';

const emailRe = /([a-z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*)/gi;

const HubspotContainer = styled.div`
  text-align: left;
`;

const ErrorMessage = styled.div`
`;

const EmailAddressLink = styled.a`
  color: ${colors.blue['500']};
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const EmailAddress = ({ value }) => (
  <EmailAddressLink href={`mailto:${value}`}>
    {value}
  </EmailAddressLink>
);

const HubspotForm = ({
  region,
  formId,
  sfdcCampaignId,
  redirectUrl,
  hubspotFieldOverrides,
  copyField,
  formOverrides,
  defaults = [],
  onSubmit
}) => {
  const data = useStaticQuery(graphql`
    query HubspotForm {
      allContentfulOptionSet(filter: {name: {eq: "globals"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const location = useLocation();
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const { loaded, submitted, formRef } = useHubspot({
    region,
    formId,
    sfdcCampaignId,
    redirectUrl,
    hubspotFieldOverrides,
    copyField,
    formOverrides,
    portalId: HUBSPOT_PORTAL_ID,
    defaults: [
      ...defaults,
      {
        // TODO - need to delete after workflows are migrated
        name: 'marketing_page__c',
        value: convertPathForTracking(location.pathname)
      },
      {
        name: 'latest_form_page',
        value: convertPathForTracking(location.pathname)
      },
      {
        name: 'utm_source',
        value: ''
      },
      {
        name: 'utm_medium',
        value: ''
      },
      {
        name: 'latest_marketing_page',
        value: qs.parse(location.search, { ignoreQueryPrefix: true })?.page
      },
      {
        name: 'latest_form_src_ref',
        value: ''
      },
      {
        name: 'latest_abtest',
        value: ''
      },
      {
        name: 'latest_utm_campaign',
        value: ''
      },
      {
        name: 'latest_utm_content',
        value: ''
      },
      {
        name: 'latest_utm_device',
        value: ''
      },
      {
        name: 'latest_utm_medium',
        value: ''
      },
      {
        name: 'latest_utm_source',
        value: ''
      },
      {
        name: 'latest_utm_term',
        value: ''
      },
      {
        name: 'gclid',
        value: ''
      },
    ]
  });
  const [showLoadingMessage, setShowLoadingMessage] = useState(false);
  const message = options['globals.hubspot_form_loading_message'];

  useEffect(() => {
    if (submitted && onSubmit && typeof onSubmit === 'function') {
      onSubmit();
    }
  }, [submitted, onSubmit]);

  useEffect(() => {
    let mounted = true;
    // Short delay before showing the loading message to prevent flash of text if form loads
    setTimeout(() => {
      if (mounted) {
        setShowLoadingMessage(true);
      }
    }, 800);

    return () => {
      mounted = false;
    };
  }, []);

  // Include URL Parameters In Redirect URL After Form Submission
  useEffect(() => {
    const handleMessage = (event) => {
      if (formOverrides?.includes('Send email as param')) {
        if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormSubmitted') {
          // getting the email value from form
          const input = document.querySelector('[name="email"]');
          const encodedInput = btoa(input.value); // encoding the email

          // getting the redirect URL from form
          const context = document.querySelector('input[name="hs_context"]');
          const contextObject = JSON.parse(context.value);
          // Retrieve the redirectUrl from the form settings
          const { redirectUrl: formRedirectUrl } = contextObject.originalEmbedContext;

          if (input.value) {
            if (formRedirectUrl) {
              // append the email via btoa() to URL
              window.location.href = `${formRedirectUrl}?email=${encodedInput}`;
            }
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });

  // Adding Impact Universal tracking Tag (UTT)
  useEffect(() => {
    if (formOverrides?.includes('Impact Referral')) {
      // Define the impactOnReady function
      const impactOnReady = () => {
        console.log('Impact script loaded');
        const elements = document.getElementsByName('impact_referredby_code');
        if (elements.length > 0) {
          const element = elements[0];
          window.impact
            .api()
            .referralCookie()
            .then((response) => {
              const referralCode = response.codes['26601'];
              if (referralCode) {
                element.value = referralCode;
              }
            })
            .catch((error) => {
              console.error('Error retrieving referral cookie:', error);
            });
        } else {
          console.error('No element found with name "impact_referredby_code"');
        }
      };

      // Function to load the Impact script dynamically
      const loadImpactScript = () => new Promise((resolve, reject) => {
        if (document.getElementById('impact-script')) {
          resolve();
          return; // Explicit return for consistent-return
        }

        const script = document.createElement('script');
        script.id = 'impact-script';
        script.src = 'https://utt.impactcdn.com/A5628474-671a-4ec6-99c9-ab008075da6e1.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;

        document.head.appendChild(script);
      });

      // Load Impact and then run the tracking function
      loadImpactScript()
        .then(() => {
          if (typeof window.impact !== 'undefined') {
            // 1-second delay to ensure HubSpot form fields are rendered
            setTimeout(impactOnReady, 1000);
          } else {
            console.error('Impact object not found.');
          }
        })
        .catch((error) => {
          console.error('Error loading Impact script:', error);
        });
    }
  });

  // Load and initialize FormComplete Gatekeeper Utility
  /* eslint-disable no-useless-escape */
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '/zoomInfoDomainBlacklist.js';
    script.async = true;

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        console.log('Cleaning up script');
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <HubspotContainer className="hbspt-contain">
      <div className="hubspot-styles hbspt-form" ref={formRef} />
      {!loaded && showLoadingMessage && message && (
        <ErrorMessage>
          {replaceWithComponent(emailRe, message, EmailAddress)}
        </ErrorMessage>
      )}
    </HubspotContainer>
  );
};

HubspotForm.propTypes = {
  region: PropTypes.string,
  formId: PropTypes.string.isRequired,
  sfdcCampaignId: PropTypes.string,
  redirectUrl: PropTypes.string,
  hubspotFieldOverrides: PropTypes.string,
  formOverrides: PropTypes.arrayOf(PropTypes.string),
  copyField: PropTypes.string,
  defaults: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      attr: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onSubmit: PropTypes.func
};

export default HubspotForm;
